// AuthContext.jsx
import { createContext, useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { fetchData } from '../api/api';
import { getCookie, setCookie } from '../api/cookies';
import { sendLogToServer } from '../utils/log';
import { handleError } from './errors/errorUtils';
import { Capacitor } from '@capacitor/core';
import { registerPlugin } from '@capacitor/core';
import { Device } from '@capacitor/device';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [userId, setUserId] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [platform, setPlatform] = useState('browser');
  const [isGuest, setIsGuest] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authError, setAuthError] = useState(null);
  const isNative = Capacitor.isNativePlatform();
  
  // Android специфичные данные
  const [androidData, setAndroidData] = useState({ 
    deviceId: null, 
    language: null, 
    architecture: null, 
    fcmToken: null, 
    totalMemory: 0, 
    isReady: false 
  });

	const getUtmFromUrl = () => {
	  const params = new URLSearchParams(window.location.search);
	  const v = (k) => params.get(k) || null;
	  return {
		utm_source: v("utm_source"),
		utm_medium: v("utm_medium"),
		utm_campaign: v("utm_campaign"),
		utm_content: v("utm_content"),
		utm_term: v("utm_term"),
	  };
	};

  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    if (/chrome|crios/i.test(ua)) return `Chrome ${ua.match(/(?:chrome|crios)\/([\d.]+)/i)?.[1] || ''}`;
    if (/firefox|fxios/i.test(ua)) return `Firefox ${ua.match(/(?:firefox|fxios)\/([\d.]+)/i)?.[1] || ''}`;
    if (/safari/i.test(ua) && !/chrome|crios/i.test(ua)) return `Safari ${ua.match(/version\/([\d.]+)/i)?.[1] || ''}`;
    if (/edg/i.test(ua)) return `Edge ${ua.match(/edg\/([\d.]+)/i)?.[1] || ''}`;
    return 'Unknown';
  };

  const getreferrerIdFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('referrerId', ref);
      return ref;
    }
    return localStorage.getItem('referrerId');
  };

	const initAndroidMeta = () => {
	  return new Promise((resolve) => {
		const handleAndroidMeta = (event) => {
		  const {
			deviceId,
			language,
			platform,
			architecture,
			totalMemory,
			fcmToken,
		  } = event.detail;

		  setAndroidData({
			deviceId,
			language,
			platform,
			architecture,
			totalMemory,
			fcmToken,
			isReady: true,
		  });

		  window.removeEventListener('androidMeta', handleAndroidMeta);
		  resolve({ deviceId, language, platform, architecture, totalMemory });
		};

		window.addEventListener('androidMeta', handleAndroidMeta);

		// Если мета уже пришла до монтирования
		if (window.androidMetaReceived) {
		  const {
			deviceId,
			language,
			platform,
			architecture,
			totalMemory,
			fcmToken,
		  } = window.androidMetaReceived;

		  setAndroidData({
			deviceId,
			language,
			platform,
			architecture,
			totalMemory,
			fcmToken,
			isReady: true,
		  });

		  window.removeEventListener('androidMeta', handleAndroidMeta);
		  resolve({ deviceId, language, platform, architecture, totalMemory, fcmToken });
		  return;
		}

		// Если это явно не Android — резолвим пустыми
		if (
		  typeof window.AndroidInterface === 'undefined' &&
		  !window.androidMetaReceived
		) {
		  setAndroidData({
			deviceId: null,
			language: null,
			platform: null,
			architecture: null,
			totalMemory: null,
			fcmToken: null,
			isReady: true,
		  });
		  window.removeEventListener('androidMeta', handleAndroidMeta);
		  resolve({
			deviceId: null,
			language: null,
			platform: null,
			architecture: null,
			totalMemory: null,
			fcmToken: null,
		  });
		  return;
		}

		// Фоллбэк по таймауту
		setTimeout(() => {
		  //console.warn('⚠️ Android meta timeout in AuthProvider');
		  setAndroidData({
			deviceId: null,
			language: null,
			platform: null,
			architecture: null,
			totalMemory: null,
			fcmToken: null,
			isReady: true,
		  });
		  window.removeEventListener('androidMeta', handleAndroidMeta);
		  resolve({
			deviceId: null,
			language: null,
			platform: null,
			architecture: null,
			totalMemory: null,
			fcmToken: null,
		  });
		}, 3000);
	  });
	};

	const initAuth = async (force = false) => {
	  if (isInitialized && !force) {
		return;
	  }
	  setIsLoading(true);
	  
	  try {
		const androidData = await initAndroidMeta();
		let currentPlatform;

		// Проверяем Telegram
		if (window.Telegram?.WebApp?.initData) {
		  currentPlatform = 'telegram';
		}
		// Проверяем Android
		else if (isNative || window.AndroidInterface || androidData.deviceId) {
		  currentPlatform = 'android';
		}
		// По умолчанию браузер
		else {
		  currentPlatform = 'browser';
		}

		if (isNative && currentPlatform !== 'android') {
		  sendLogToServer('⚠️ Capacitor mismatch', {
			isNative,
			currentPlatform,
			androidData,
			hasAndroidInterface: !!window.AndroidInterface,
		  });
		}

		setPlatform(currentPlatform);

		let telegramUser = null;
		let telegramId = null;

		if (currentPlatform === 'telegram') {
		  try {
			const tg = window.Telegram.WebApp;
			tg.ready();
			tg.expand();
			telegramUser = tg.initDataUnsafe?.user || null;
			telegramId = telegramUser?.id || null;
		  } catch (error) {
			handleError({
			  code: 'TELEGRAM_INIT_ERROR',
			  error,
			  context: { module: 'AuthProvider' },
			});
		  }
		}

		// Device ID
		let deviceId;
		if (currentPlatform === 'android' && androidData.deviceId) {
		  deviceId = androidData.deviceId;
		} else if (currentPlatform === 'android') {
		  try {
			const { identifier } = await Device.getId();
			deviceId = identifier || uuidv4();
			if (deviceId) localStorage.setItem('deviceId', deviceId);
		  } catch (error) {
			deviceId = localStorage.getItem('deviceId') || uuidv4();
			if (!localStorage.getItem('deviceId')) {
			  localStorage.setItem('deviceId', deviceId);
			}
		  }
		} else {
		  deviceId = localStorage.getItem('deviceId') || uuidv4();
		  if (!localStorage.getItem('deviceId')) {
			localStorage.setItem('deviceId', deviceId);
		  }
		}

		let referrerId = getreferrerIdFromUrl();
		const storedUserId = getCookie('userId');
		const storedTempUserId = getCookie('tempUserId');

		let finalUserId = null;
		let finalUserInfo = null;

		// Проверка существующего пользователя
		if (storedUserId || telegramId || storedTempUserId || androidData.deviceId || deviceId) {
		  const authResponse = await fetchData('/api/auth/check', 'POST', {
			userId: storedUserId,
			tempUserId: storedTempUserId,
			telegramId,
			deviceId,
			fcmToken: androidData.fcmToken,
			platform: currentPlatform,
			androidLanguage: androidData.language,
		  });

		  if (authResponse?.isValid && (authResponse.userId || authResponse.tempUserId)) {
			//console.log('[initAuth] valid auth, user found');
			if (authResponse.userId) {
			  finalUserId = authResponse.userId;
			  finalUserInfo = { ...authResponse.userInfo, androidLanguage: androidData.language };
			  setCookie('userId', finalUserId, 30);
			  setIsGuest(false);
			} else {
			  finalUserId = authResponse.tempUserId;
			  finalUserInfo = { isTemporary: true, ...authResponse.userInfo, androidLanguage: androidData.language };
			  setCookie('tempUserId', finalUserId, 7);
			  setIsGuest(true);
			}

			if (authResponse.token) {
			  setCookie('authToken', authResponse.token, finalUserId ? 30 : 7);
			}

			setUserId(finalUserId);
			setUserInfo(finalUserInfo);
			setIsInitialized(true);
			return;
		  } else {
			document.cookie = 'userId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
			document.cookie = 'tempUserId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
			document.cookie = 'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
		  }
		}

		// Регистрация нового пользователя
		if (currentPlatform === 'browser') {
		  const browserType = getBrowserInfo();
		  const utm = getUtmFromUrl(); 
		  const welcomeStepsTotal = 1;

		  const registerResponse = await fetchData('/api/register/temporary', 'POST', {
			deviceId,
			browserType,
			referrerId,
			platform: currentPlatform,
			utm,
			welcomeStepsTotal                   
		  });		
		
		  if (registerResponse?.tempUserId) {
			finalUserId = registerResponse.tempUserId;
			finalUserInfo = { isTemporary: true, ...registerResponse.userInfo };
			setUserId(finalUserId);
			setUserInfo(finalUserInfo);
			setCookie('tempUserId', finalUserId, 7);
			if (registerResponse.token) setCookie('authToken', registerResponse.token, 7);
			setIsGuest(true);
			setIsInitialized(true);
			//console.log('[initAuth] browser user registered', { finalUserId, finalUserInfo });
			return;
		  }
		}

		if (['android', 'telegram'].includes(currentPlatform)) {
		  const isAndroid = currentPlatform === 'android';
		  const isTelegram = currentPlatform === 'telegram';

		  if (currentPlatform === 'android') {
			try {
			  const InstallReferrer = registerPlugin('InstallReferrer');
			  const { referrer } = await InstallReferrer.getReferrer();
			  if (referrer) referrerId = referrer;
			} catch (err) {
			  referrerId = null;
			}
		  }

		  const registerPayload = {
			platform: currentPlatform,
			referrerId,
			...(isAndroid && { deviceId }),
			...(isTelegram && { telegramId, deviceId }),
		  };

		  try {
			const registerResponse = await fetchData('/api/register', 'POST', registerPayload);

			if (registerResponse?.userId) {
			  finalUserId = registerResponse.userId;
			  finalUserInfo = isAndroid
				? { ...registerResponse.userInfo, androidLanguage: androidData.language }
				: registerResponse.userInfo || telegramUser || {};
			  setUserId(finalUserId);
			  setUserInfo(finalUserInfo);
			  setCookie('userId', finalUserId, 30);
			  if (registerResponse.token) setCookie('authToken', registerResponse.token, 30);
			  setIsGuest(false);
			  setIsInitialized(true);
			  sendLogToServer('register_log', `${currentPlatform} user registered`, {
				platform: currentPlatform,
				userId: finalUserId,
				...(isAndroid && { deviceId, deviceLanguage: androidData.language }),
				...(isTelegram && { telegramId }),
			  });
			  return;
			}
			throw new Error('Registration failed for all platforms');
		  } catch (error) {
			handleError({
			  code: 'REGISTER_REQUEST_ERROR',
			  error,
			  context: { module: 'AuthProvider', endpoint: '/api/register', payload: registerPayload },
			  showToUser: true,
			});
			setUserId(null);
			setAuthError('Registration error');
		  }
		}

		throw new Error('Authentication initialization failed');
	  } catch (error) {
		handleError({
		  code: 'AUTH_INIT_ERROR',
		  error,
		  context: { module: 'AuthProvider' },
		  showToUser: true,
		});
		setUserId(null);
		setAuthError('Authorization error');
	  } finally {
		setIsLoading(false);
	  }
	};

	useEffect(() => {
	  initAuth();
	}, []);
	
	useEffect(() => {
	  const params = new URLSearchParams(window.location.search);
	  if (params.get('auth') === '1') {
		initAuth(true);

		// убрать auth=1 из URL, чтобы не повторять
		params.delete('auth');
		const newQuery = params.toString();
		const newUrl = window.location.pathname + (newQuery ? `?${newQuery}` : '');
		window.history.replaceState({}, '', newUrl);
	  }
	  // eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	  	
	return (
	  <AuthContext.Provider
		value={{
		  userId,
		  userInfo: { ...userInfo, userId },
		  platform,
		  isLoading,
		  isGuest,
		  setUserInfo,
		  authError,
		  initAuth,
		  androidData,
		  isAndroidApp: platform === 'android',
		  deviceId: androidData.deviceId || localStorage.getItem('deviceId'),
		  deviceLanguage: androidData.language,
		  deviceArch: androidData.architecture,
		  deviceMemory: androidData.totalMemory,
		}}
	  >
		{children}
	  </AuthContext.Provider>
	);
};


export const useAuth = () => {
  const context = useContext(AuthContext);
  return context || {
    userId: null,
    userInfo: null,
    platform: '',
    isLoading: false,
    isGuest: false,
    initAuth: () => {},
    androidData: { deviceId: null, language: null, isReady: false },
    isAndroidApp: false,
    deviceId: null,
    deviceLanguage: null,
	deviceArch: null,
	deviceMemory: null,	
  };
};
